// eslint-disable-next-line no-unused-vars
import React from 'react';
import { ttlCache } from '../utils/misc';

const maxObjectJson = 2 * 1024;
const NOP = (...args) => NOP.cache.push(args);
NOP.cache = [];

let domain = null;
let app = null;
let lastUrl = null;
function getDomain(http) {
  if (http.defaults.baseURL != null || http.defaults.baseURL !== lastUrl) {
    const domainPattern = /^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?]+)/gim;
    const match = domainPattern.exec(http.defaults.baseURL);
    lastUrl = http.defaults.baseURL;
    app =
      (createLogger.rootLogger && createLogger.rootLogger.config && createLogger.rootLogger.config.app) || 'unknown';
    if (match) {
      // eslint-disable-next-line prefer-destructuring
      domain = match[1];
      return domain;
    }
    if (window && window.location && window.location.host) {
      domain = window.location.host;
    }
  }
  return domain || 'unknown';
}

// [TODO]: Remove forceSend functionality after 2020-9-30
const allowForcedSendlog = new Date(2020, 8, 30).getTime() > Date.now();

function createSendLog(timeToLive = 60 * 1000, delay = 0) {
  const { http } = getLogger('root');
  let handle = null;
  // Do only send data if we have the right http client in place.
  if (http && http.defaults.baseURL != null) {
    const result = function sendLogInner(...args) {
      if (handle) {
        clearTimeout(handle);
      }
      const forceSend =
        allowForcedSendlog && args.length > 0 && args[args.length - 1] != null && args[args.length - 1].forceSend;
      result.cache.push(args);
      if (forceSend || (delay > -1 && (delay > 0 || args[2] <= LogLevel.Error))) {
        handle = setTimeout(
          () => {
            // Refetch reference to current instance of http client.
            const { http } = getLogger('root');
            const cacheEntries = result.cache.popAll();
            const logEntries = cacheEntries.map(args => {
              const [timestamp, source, level, ...messageObjects] = args;
              const message = messageObjects.reduce((current, value) => {
                if (value) {
                  const result = current.length > 0 ? current + ' ' : '';
                  const valueType = typeof value;
                  if (valueType !== 'string' && valueType !== 'number' && valueType !== 'boolean') {
                    const json = JsonStringifySafe(value);
                    if (json && json.length > maxObjectJson) {
                      return result + json.substring(0, maxObjectJson) + `[Large Object ${json.length} Bytes]`;
                    }
                    return result + (json != null ? json : '');
                  }
                  return result + value;
                }
                return current;
              }, '');
              return {
                domain: getDomain(http),
                app,
                timestamp,
                source,
                level,
                message,
              };
            });
            http
              .post(`/api/logs/clients.json`, logEntries)
              .then(response => response)
              .catch(console.warn);
          },
          delay > 0 ? delay : 0
        );
      }
    };
    result.cache = ttlCache(timeToLive);
    NOP.cache.splice(0, NOP.cache.length).forEach(result.cache.push);
    delete NOP.cache;
    return result;
  }
  return NOP;
}
let sendLog = NOP;

function toLevelString(level) {
  switch (level) {
    case LogLevel.Error:
      return 'ERROR';
    case LogLevel.Warning:
      return 'WARN';
    case LogLevel.Info:
      return 'INFO';
    case LogLevel.Debug:
      return 'DEBUG';
    case LogLevel.Trace:
      return 'TRACE';
    default:
      return 'UNDEFINED';
  }
}
function toLevelColor(level) {
  switch (level) {
    case LogLevel.Error:
      return '#dc3545';
    case LogLevel.Warning:
      return '#ffc107';
    case LogLevel.Info:
      return '#17a2b8';
    case LogLevel.Debug:
      return '#28a745';
    case LogLevel.Trace:
      return '#6f42c1';
    default:
      return '#fd7e14';
  }
}

function buildLogArguments(name, level, args) {
  let result = `%c${new Date().toISOString()} [${name}] ${level} -`;
  const logParams = [];
  args.forEach(arg => {
    const type = typeof arg;
    if (type === 'string') {
      result += ' ' + arg;
    } else {
      result += ' %o';
      logParams.push(arg);
    }
  });
  return [result, 'color: #dc3545', ...logParams];
}

const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
      if (value instanceof Error && value.stack) {
        return value.stack;
      }
    }
    return value;
  };
};

export function JsonStringifySafe(obj, space) {
  return JSON.stringify(obj, getCircularReplacer(), space);
}

export const LogLevel = {
  Off: -1,
  NoLog: 0,
  Fatal: 1,
  Error: 2,
  Warning: 3,
  Info: 4,
  Debug: 5,
  Trace: 6,
};

function Logger(name, config = { level: LogLevel.Debug }, rootLogger) {
  this.name = name;
  this.config = config;
  this.rootLogger = rootLogger;
  const self = this;
  const logWith = function(level, ...args) {
    if (sendLog === NOP) {
      const config = (self.rootLogger && self.rootLogger.config) || self.config || {};
      if (self.http) {
        sendLog = createSendLog(config.timeToLive, config.delay);
      } else if (self.rootLogger && self.rootLogger.http) {
        sendLog = createSendLog(config.timeToLive, config.delay);
      }
    }
    if (level < LogLevel.Trace) {
      sendLog(new Date().toISOString(), self.name, level, ...args);
    }
    if (!self.isEnabled(level)) {
      return;
    }
    switch (level) {
      case LogLevel.Fatal:
        if (self.dontUseErrorLog()) {
          console.log(...buildLogArguments(self.name, 'FATAL', args));
        } else {
          console.error(`${new Date().toISOString()}`, `[${self.name}]`, 'FATAL', '-', ...args);
        }
        break;
      case LogLevel.Error:
        if (self.dontUseErrorLog()) {
          console.log(...buildLogArguments(self.name, 'ERROR', args));
        } else {
          console.error(`${new Date().toISOString()}`, `[${self.name}]`, 'ERROR', '-', ...args);
        }
        break;
      case LogLevel.Warning:
        if (self.dontUseErrorLog()) {
          console.log(
            `%c${new Date().toISOString()} [${self.name}] WARN -`,
            'color: ' + toLevelColor(LogLevel.Warning),
            ...args
          );
        } else {
          console.warn(`${new Date().toISOString()}`, `[${self.name}]`, 'WARN', '-', ...args);
        }
        break;
      default:
        console.log(
          `${new Date().toISOString()} %c[${self.name}] %c${toLevelString(level)}`,
          'color: #666',
          'color: ' + toLevelColor(level),
          '-',
          ...[...args]
        );
    }
  };
  this.fatal = function(...args) {
    logWith(LogLevel.Fatal, ...args);
  };
  this.error = function(...args) {
    logWith(LogLevel.Error, ...args);
  };
  this.warn = function(...args) {
    logWith(LogLevel.Warning, ...args);
  };
  this.info = function(...args) {
    logWith(LogLevel.Info, ...args);
  };
  this.debug = function(...args) {
    logWith(LogLevel.Debug, ...args);
  };
  this.trace = function(...args) {
    logWith(LogLevel.Trace, ...args);
  };

  this.isFatalEnabled = function() {
    return self.isEnabled(LogLevel.Fatal);
  };
  this.isErrorEnabled = function() {
    return self.isEnabled(LogLevel.Error);
  };
  this.isWarningEnabled = function() {
    return self.isEnabled(LogLevel.Warning);
  };
  this.isInfoEnabled = function() {
    return self.isEnabled(LogLevel.Info);
  };
  this.isDebugEnabled = function() {
    return self.isEnabled(LogLevel.Debug);
  };
  this.isTraceEnabled = function() {
    return self.isEnabled(LogLevel.Trace);
  };
  this.isEnabled = function(level) {
    return (
      (self.rootLogger &&
        self.rootLogger.config.level > -1 &&
        ((self.rootLogger.config[self.name] && level <= self.rootLogger.config[self.name].level) ||
          level <= self.config.level)) ||
      level <= self.config.level
    );
  };
  this.dontUseErrorLog = function() {
    return (
      (self.rootLogger && self.rootLogger.config[self.name] && self.rootLogger.config[self.name].noErrorLog) ||
      self.config.noErrorLog
    );
  };
}

export function createLogger(name, config, internal) {
  if (!name || name === 'root') {
    if (!createLogger.rootLogger) {
      createLogger.rootLogger = new Logger(name, config);
      createLogger.rootLogger.loggers = { root: createLogger.rootLogger };
      createLogger.rootLogger.getLogger = (name, config) =>
        createLogger.rootLogger.loggers[name] ||
        createLogger(
          name,
          config ? Object.assign(config, createLogger.rootLogger.config) : createLogger.rootLogger.config
        );
    }
    if (createLogger.rootLogger && !internal && config) {
      createLogger.rootLogger.config = Object.assign(createLogger.rootLogger.config, config);
    }
    return createLogger.rootLogger;
  }
  if (!createLogger.rootLogger) {
    createLogger('root');
  }
  const logger = new Logger(name, config, createLogger.rootLogger);
  createLogger.rootLogger.loggers[name] = logger;
  return logger;
}

export function getLogger(name, config) {
  if (!name) {
    return createLogger('root', undefined, true);
  }
  const rootLogger = createLogger('root');
  return rootLogger.getLogger(name, config);
}
